// 纯数据 API 客户端
const API_BASE = process.env.REACT_APP_API_URL || 'https://fakawang-api.512faka.workers.dev';

// 通用请求函数
const request = async (endpoint, body) => {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }
    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 核心 CRUD 操作
const api = {
  // 查询数据
  query: (table, where = {}, orderBy = '', limit = null) => {
    return request('/api/q', { t: table, w: where, o: orderBy, l: limit })
      .then(res => res.d || []);
  },

  // 插入数据
  insert: (table, data) => {
    return request('/api/i', { t: table, d: data });
  },

  // 更新数据
  update: (table, data, where) => {
    return request('/api/u', { t: table, d: data, w: where });
  },

  // 删除数据
  delete: (table, where) => {
    return request('/api/d', { t: table, w: where });
  },

  // 健康检查
  health: async () => {
    const res = await fetch(`${API_BASE}/health`);
    return res.json();
  },

  // GET 请求
  get: async (endpoint, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_BASE}${endpoint}${queryString ? '?' + queryString : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      return data;
    } catch (error) {
      console.error('GET Error:', error);
      throw error;
    }
  },

  // POST 请求
  post: async (endpoint, body = {}) => {
    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      return data;
    } catch (error) {
      console.error('POST Error:', error);
      throw error;
    }
  }
};

// 业务封装层
export const productAPI = {
  // 获取商品列表（包含实时卡密数量）
  getList: async (activeOnly = true) => {
    const where = activeOnly ? { isActive: 1 } : {};
    const products = await api.query('Products', where, 'createdAt DESC');
    
    // 为每个商品查询可用卡密数量
    const productsWithCardCount = await Promise.all(
      products.map(async (product) => {
        try {
          const cards = await api.query('Cards', { 
            productId: product.id, 
            status: 'available' 
          });
          return {
            ...product,
            stock: cards.length // 用实际可用卡密数量替代stock字段
          };
        } catch (err) {
          console.error(`获取商品${product.id}的卡密数量失败:`, err);
          return product;
        }
      })
    );
    
    return productsWithCardCount;
  },

  // 获取商品详情
  getById: async (id) => {
    const list = await api.query('Products', { id }, '', 1);
    return list[0] || null;
  },

  // 创建商品
  create: (data) => {
    return api.insert('Products', {
      ...data,
      isActive: 1,
      stock: data.stock || 0
    });
  },

  // 更新商品
  update: (id, data) => {
    return api.update('Products', data, { id });
  },

  // 删除商品
  delete: (id) => {
    return api.delete('Products', { id });
  }
};

export const userAPI = {
  // 用户注册
  register: async (username, password, email) => {
    // 检查用户是否存在
    const existing = await api.query('Users', { username }, '', 1);
    if (existing.length > 0) {
      throw new Error('用户名已存在');
    }

    // 创建用户（密码明文，生产环境应加密）
    const result = await api.insert('Users', {
      username,
      password,
      email: email || null,
      role: 'user'
    });

    return { success: true, userId: result.id };
  },

  // 用户登录
  login: async (username, password) => {
    // 使用专用登录端点
    const response = await fetch(`${API_BASE}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    
    const data = await response.json();
    
    if (data.error || !data.success) {
      throw new Error(data.error || '用户名或密码错误');
    }

    const user = data.user;
    const token = `${user.id}.${user.username}.${user.role}`;
    
    // 存储到 localStorage
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));

    return { success: true, token, user };
  },

  // 获取当前用户
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // 退出登录
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

export const orderAPI = {
  // 获取用户订单
  getMyOrders: async () => {
    const user = userAPI.getCurrentUser();
    if (!user) throw new Error('未登录');
    
    return api.query('Orders', { userId: user.id }, 'createdAt DESC');
  },

  // 获取所有订单（管理员）
  getAllOrders: () => {
    return api.query('Orders', {}, 'createdAt DESC');
  },

  // 分页查询订单（管理员）
  getOrdersWithPagination: (page = 1, pageSize = 20) => {
    return api.get('/api/admin/orders', { page, pageSize });
  },

  // 创建订单（支持游客购买）
  create: async (productId, totalAmount, quantity = 1, contactEmail = '', paymentMethod = 'usdt') => {
    const user = userAPI.getCurrentUser();

    // 生成订单号
    const orderNo = 'ORD' + Date.now() + Math.random().toString(36).substr(2, 9).toUpperCase();

    const result = await api.insert('Orders', {
      orderNo,
      userId: user ? user.id : null,
      productId,
      quantity,
      totalAmount,
      status: 'pending',
      paymentMethod: paymentMethod,
      contactEmail
    });

    return {
      ...result,
      orderNo
    };
  },

  // 更新订单状态
  updateStatus: (orderId, status, cardCode = null) => {
    const data = { status };
    if (cardCode) data.cardCode = cardCode;
    return api.update('Orders', data, { id: orderId });
  },

  // 查询订单状态（通过订单号）
  checkOrderStatus: (orderNo) => {
    return api.query('Orders', { orderNo });
  },

  // 创建微信支付订单
  createWechatPayment: async (paymentData) => {
    return request('/api/wechat/pay', paymentData);
  },

  // 创建USDT支付订单
  createUsdtPayment: async (paymentData) => {
    return request('/api/usdt/pay', paymentData);
  },

  // 检查USDT支付状态
  checkUsdtPayment: async (orderNo, txHash = null) => {
    return request('/api/usdt/check', { orderNo, txHash });
  }
};

export const cardAPI = {
  // 获取商品的卡密列表
  getByProduct: (productId, unusedOnly = true) => {
    const where = { productId };
    if (unusedOnly) where.status = 'available';
    return api.query('Cards', where, 'createdAt DESC');
  },

  // 添加卡密
  create: (data) => {
    return api.insert('Cards', data);
  },

  // 标记卡密已使用
  markUsed: (cardId, orderId) => {
    return api.update('Cards', {
      status: 'sold',
      orderId
    }, { id: cardId });
  },

  // 删除卡密
  delete: (id) => {
    return api.delete('Cards', { id });
  },

  // 获取商品信息
  getProductInfo: (productId) => {
    return api.query('Products', { id: productId });
  },

  // 更新商品库存
  updateProductStock: (productId, stock) => {
    return api.update('Products', { stock }, { id: productId });
  }
};

export default api;
